import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  PlusIcon,
  CreditCardIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import AuthHeader from '../components/AuthHeader'
import HotelRefCard from '../components/HotelRefCard'
import FlightRefCard from '../components/FlightRefCard'
import { buildApiUrl } from '../config/api'

function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [recentRedemptions, setRecentRedemptions] = useState([])
  const [analyticsData, setAnalyticsData] = useState(null)
  const [loading, setLoading] = useState(false)

  // Sample data for guest users
  const sampleRedemptions = [
    {
      id: 1,
      program: 'American Airlines AAdvantage',
      type: 'Flight',
      points: 25000,
      cashValue: 500,
      cpp: 0.020,
      date: '2024-01-15',
      route: 'LAX → JFK'
    },
    {
      id: 2,
      program: 'Marriott Bonvoy',
      type: 'Hotel',
      points: 35000,
      cashValue: 350,
      cpp: 0.010,
      date: '2024-01-10',
      location: 'New York'
    },
    {
      id: 3,
      program: 'Delta SkyMiles',
      type: 'Flight',
      points: 40000,
      cashValue: 800,
      cpp: 0.020,
      date: '2024-01-05',
      route: 'SFO → LHR'
    }
  ]

  // Calculate real program distribution from user data
  const calculateProgramDistribution = () => {
    if (!analyticsData || !analyticsData.recentRedemptions || analyticsData.recentRedemptions.length === 0) {
      if (!recentRedemptions || recentRedemptions.length === 0) {
        return [
          { name: 'No Programs', value: 100, color: '#9CA3AF' }
        ];
      }
    }

    // Use analytics data if available, otherwise fall back to recent redemptions
    const dataToUse = analyticsData?.recentRedemptions || recentRedemptions;
    
    // Group redemptions by program and calculate percentages
    const programCounts = {};
    dataToUse.forEach(redemption => {
      const programName = redemption.program?.name || redemption.program || 'Unknown Program';
      programCounts[programName] = (programCounts[programName] || 0) + 1;
    });

    const total = Object.values(programCounts).reduce((sum, count) => sum + count, 0);
    const colors = ['#548BCA', '#6BA3D1', '#7BB3D8', '#8BC3DF', '#9BD3E6', '#ABE3ED'];
    
    return Object.entries(programCounts).map(([name, count], index) => ({
      name,
      value: Math.round((count / total) * 100),
      color: colors[index % colors.length]
    }));
  };

  const programDistribution = useMemo(() => calculateProgramDistribution(), [analyticsData, recentRedemptions]);

  // Calculate real metrics from user data
  const calculateMetrics = () => {
    if (!analyticsData) {
      return [
        { 
          id: 1, 
          title: 'Total Redemptions', 
          value: '0', 
          change: 'No redemptions yet',
          icon: CreditCardIcon,
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600'
        },
        { 
          id: 2, 
          title: 'Average CPP', 
          value: '0.0¢', 
          change: 'No data available',
          icon: ChartBarIcon,
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600'
        },
        { 
          id: 3, 
          title: 'Value Saved', 
          value: '$0', 
          change: 'No redemptions yet',
          icon: CurrencyDollarIcon,
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600'
        },
        { 
          id: 4, 
          title: 'Total Points Spent', 
          value: '0', 
          change: 'No points spent yet',
          icon: ChartBarIcon,
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600'
        }
      ];
    }

    // Use analytics data for accurate metrics
    const totalRedemptions = Number(analyticsData.totalRedemptions) || 0;
    const totalPoints = Number(analyticsData.totalPointsSpent) || 0;
    const totalValue = Number(analyticsData.totalCashValue) || 0;
    const totalCPP = Number(analyticsData.averageCPP) || 0;
    const uniquePrograms = Number(analyticsData.uniquePrograms) || 0;

    // Ensure all values are numbers
    // console.log('Dashboard: Analytics values:', {
    //   totalRedemptions,
    //   totalPoints,
    //   totalValue,
    //   totalCPP,
    //   uniquePrograms,
    //   totalValueType: typeof totalValue
    // });

    return [
      { 
        id: 1, 
        title: 'Total Redemptions', 
        value: totalRedemptions.toString(), 
                  change: totalRedemptions > 0 ? totalRedemptions + ' redemption' + (totalRedemptions > 1 ? 's' : '') : 'No redemptions yet',
        icon: CreditCardIcon,
        iconBg: totalRedemptions > 0 ? 'bg-blue-100' : 'bg-gray-100',
        iconColor: totalRedemptions > 0 ? 'text-blue-600' : 'text-gray-600'
      },
      { 
        id: 2, 
        title: 'Average CPP', 
                  value: (typeof totalCPP === 'number' ? totalCPP.toFixed(2) : '0.00') + '¢', 
        change: totalCPP > 0 ? 'Based on your data' : 'No data available',
        icon: ChartBarIcon,
        iconBg: totalCPP > 0 ? 'bg-blue-100' : 'bg-gray-100',
        iconColor: totalCPP > 0 ? 'text-blue-600' : 'text-gray-600'
      },
      { 
        id: 3, 
        title: 'Value Saved', 
                  value: '$' + (typeof totalValue === 'number' ? totalValue.toFixed(0) : '0'), 
                  change: totalValue > 0 ? 'Total value from ' + totalRedemptions + ' redemption' + (totalRedemptions > 1 ? 's' : '') : 'No redemptions yet',
        icon: CurrencyDollarIcon,
        iconBg: totalValue > 0 ? 'bg-blue-100' : 'bg-gray-100',
        iconColor: totalValue > 0 ? 'text-blue-600' : 'text-gray-600'
      },
      { 
        id: 4, 
        title: 'Total Points Spent', 
        value: totalPoints.toLocaleString(), 
        change: totalPoints > 0 ? totalPoints.toLocaleString() + ' points spent' : 'No points spent yet',
        icon: ChartBarIcon,
        iconBg: totalPoints > 0 ? 'bg-blue-100' : 'bg-gray-100',
        iconColor: totalPoints > 0 ? 'text-blue-600' : 'text-gray-600'
      }
    ];
  };

  // Calculate metrics whenever analyticsData changes
  const redemptionCards = useMemo(() => calculateMetrics(), [analyticsData]);

  useEffect(() => {
    if (user) {
      fetchAnalyticsData()
      fetchRecentRedemptions()
    } else {
      setRecentRedemptions(sampleRedemptions)
    }
  }, [user])

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(buildApiUrl('/api/analytics/overview'), {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // console.log('Dashboard: Fetched analytics data:', data)
        setAnalyticsData(data.overview)
      } else {
        // console.log('Dashboard: Analytics API response not ok')
        setAnalyticsData(null)
      }
    } catch (error) {
      // console.error('Error fetching analytics data:', error)
      setAnalyticsData(null)
    }
  }

  const fetchRecentRedemptions = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      // Use the authenticated endpoint that automatically filters by user
      const response = await fetch(buildApiUrl('/api/redemptions?limit=3&orderBy=created_at&sort=desc'), {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        // console.log('Dashboard: Fetched user redemptions data:', data)
        setRecentRedemptions(data.redemptions || [])
      } else {
        // console.log('Dashboard: API response not ok, setting empty array for authenticated user')
        setRecentRedemptions([])
      }
    } catch (error) {
      // console.error('Error fetching recent redemptions:', error)
      setRecentRedemptions([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddRedemption = () => {
    if (user) {
      navigate('/compare-redemption')
    } else {
      navigate('/register')
    }
  }

  // Only use sample data for guest users, authenticated users should only see real data
  const data = user ? (recentRedemptions || []) : sampleRedemptions
  
  // Debug logging
      // console.log('Dashboard: Current data:', { user, recentRedemptions, sampleRedemptions, data })

    try {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #D9E4F8, #ffffff)' }}>
        {/* Header */}
        <AuthHeader />

        {/* Hero Section */}
        <section className="py-20 flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-2xl md:text-3xl text-gray-900 uppercase" style={{ lineHeight: '1.4', fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.05em' }}>
              MY REDEMPTIONS
            </h1>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Top Section - Two Cards Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* My Redemptions Card */}
              <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md" style={{ backgroundColor: '#F7FAFF' }}>
                <h3 className="text-lg text-gray-800 mb-6" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                  REDEMPTION METRICS
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {redemptionCards.map((card) => {
                    const IconComponent = card.icon;
                    return (
                      <div key={card.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center">
                          <div className={'p-3 ' + card.iconBg + ' rounded-lg'}>
                            <IconComponent className={'w-6 h-6 ' + card.iconColor} />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>{card.title}</p>
                            <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400' }}>{card.value}</p>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center text-sm">
                          <ArrowUpIcon className="w-4 h-4 text-success-500 mr-1" />
                          <span className="text-success-600" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>{card.change}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Program Distribution Card */}
              <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md" style={{ backgroundColor: '#F7FAFF' }}>
                <h2 className="text-lg text-gray-800 mb-6" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                  PROGRAM DISTRIBUTION
                </h2>
                <div className="flex items-center space-x-8">
                  <div className="flex-1">
                    <div className="space-y-3">
                      {programDistribution.map((program, index) => (
                        <div key={program.name} className="flex items-center space-x-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: program.color }}
                          ></div>
                          <span className="text-sm text-gray-700" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>{program.name}</span>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400' }}>{program.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={programDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {programDistribution.map((entry, index) => (
                            <Cell key={'cell-' + index} fill={entry.color} />
                          ))}
                        </Pie>
                                                  <Tooltip formatter={(value) => value + '%'} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section - Recent Redemptions */}
            <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md" style={{ backgroundColor: '#F7FAFF' }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg text-gray-800" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                  RECENT REDEMPTIONS
                </h2>
                <button 
                  onClick={handleAddRedemption}
                  className="inline-flex items-center justify-center px-8 py-4 text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
                  style={{ backgroundColor: '#548BCB' }}
                >
                  <PlusIcon className="w-6 h-6 mr-2" />
                  <span style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>Add Redemption</span>
                </button>
              </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {loading ? (
              // Loading state
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-gray-100 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : data && data.length > 0 ? (
              // Actual redemptions using proper ref cards
              data.map((redemption) => {
                if (!redemption || typeof redemption !== 'object') {
                  // console.warn('Dashboard: Invalid redemption data:', redemption)
                  return null
                }

                // Determine if it's a hotel or flight redemption
                if (redemption.hotel) {
                  // For hotels, show the HotelRefCard
                  return (
                    <HotelRefCard
                      key={redemption.id}
                      hotel={redemption.hotel.hotel}
                      checkInDate={redemption.hotel.check_in_date}
                      checkOutDate={redemption.hotel.check_out_date}
                      price={redemption.cash_price || redemption.cashValue || 0}
                      points={redemption.points_spent || redemption.points || 0}
                      cpp={redemption.value_cpp || redemption.cpp || 0}
                      addedDate={redemption.created_at || redemption.date || 'Date not set'}
                    />
                  )
                } else if (redemption.flight) {
                  // For flights, show the FlightRefCard
                  return (
                    <FlightRefCard
                      key={redemption.id}
                      flight={redemption.flight}
                      price={redemption.cash_price || redemption.cashValue || 0}
                      points={redemption.points_spent || redemption.points || 0}
                      cpp={redemption.value_cpp || redemption.cpp || 0}
                      addedDate={redemption.created_at || redemption.date || 'Date not set'}
                      loyaltyCurrency={redemption.program?.loyalty_currency}
                    />
                  )
                } else {
                  // Fallback for unknown redemption types
                  return (
                    <div key={redemption.id} className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <CreditCardIcon className="w-5 h-5" style={{ color: '#548BCB' }} />
                          <span className="text-sm font-medium text-gray-700" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>
                            {redemption.program?.name || redemption.program || 'Loyalty Program'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>
                          {redemption.travel_start_date || redemption.date || 'Date not set'}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>Points:</span>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400' }}>
                            {(redemption.points_spent || redemption.points || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>Value:</span>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400' }}>
                            {(redemption.cash_price || redemption.cashValue || 0).toFixed(0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>CPP:</span>
                          <span className="text-sm font-medium text-gray-900" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400' }}>
                            {(redemption.value_cpp || redemption.cpp || 0).toFixed(2)}¢
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                }
              }).filter(Boolean)
            ) : (
              // No redemptions state
              <div className="col-span-full text-center py-8">
                <CreditCardIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 mb-2" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>No redemptions found</p>
                <p className="text-sm text-gray-400" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>Start tracking your loyalty program redemptions to see them here</p>
              </div>
            )}
          </div>

            </div>
          </div>
        </section>
      </div>
    )
  } catch (error) {
          // console.error('Error rendering Dashboard component:', error)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #D9E4F8, #ffffff)' }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.05em' }}>Something went wrong</h1>
          <p className="text-gray-600 mb-4" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>We're having trouble loading the dashboard page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="inline-flex items-center justify-center px-8 py-4 text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
            style={{ backgroundColor: '#548BCB' }}
          >
            <span style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em' }}>Reload Page</span>
          </button>
        </div>
      </div>
    )
  }
}

export default Dashboard